/**
 * Statistisk analys för skattekonto-data
 * Beräknar nyckelmetrics per skattetyp för att berika AI-prompt
 */

export interface TaxTypeStatistics {
  tax_type: string; // "Arbetsgivaravgifter", "Preliminärskatt", "Moms", etc.
  count: number;
  monthly_median: number;
  monthly_average: number;
  std_deviation: number;
  min: number;
  max: number;
  coefficient_of_variation: number; // std_dev / mean (nollställ om mean=0)
  monthly_breakdown: Record<string, {
    count: number;
    total: number;
    average: number;
  }>; // "2024-01" → { count, total, average }
}

export interface PaymentBehaviorStats {
  total_payments: number;
  late_payments: number;
  on_time_payments: number;
  average_days_late: number;
  max_days_late: number;
  interest_charges_count: number;
  fee_charges_count: number;
}

export interface StatisticalSummary {
  tax_types: TaxTypeStatistics[];
  payment_behavior: PaymentBehaviorStats;
  analysis_period: {
    start_date: string;
    end_date: string;
    months_covered: number;
  };
}

/**
 * Huvudfunktion: Beräkna all statistik för en batch
 */
export function calculateBatchStatistics(
  normalizedTransactions: Array<{
    id: string;
    date: string | null;
    amount: number | null;
    event: string | null;
    due_date?: string | null;
    [key: string]: any;
  }>
): StatisticalSummary {
  
  // Filtrera bort transaktioner med saknade kritiska fält
  const validTransactions = normalizedTransactions.filter(
    t => t.date && t.amount !== null && t.event
  );

  if (validTransactions.length === 0) {
    return getEmptyStatistics();
  }

  // Beräkna statistik per skattetyp
  const taxTypeStats = calculateTaxTypeStatistics(validTransactions);
  
  // Beräkna betalningsbeteende
  const paymentStats = calculatePaymentBehaviorStats(validTransactions);
  
  // Identifiera analysperiod
  const dates = validTransactions
    .map(t => new Date(t.date!))
    .filter(d => !isNaN(d.getTime()))
    .sort((a, b) => a.getTime() - b.getTime());
  
  const startDate = dates[0]?.toISOString().split('T')[0] || 'unknown';
  const endDate = dates[dates.length - 1]?.toISOString().split('T')[0] || 'unknown';
  const monthsCovered = dates.length > 0 
    ? Math.ceil((dates[dates.length - 1].getTime() - dates[0].getTime()) / (1000 * 60 * 60 * 24 * 30))
    : 0;

  return {
    tax_types: taxTypeStats,
    payment_behavior: paymentStats,
    analysis_period: {
      start_date: startDate,
      end_date: endDate,
      months_covered: monthsCovered
    }
  };
}

/**
 * Beräkna statistik per skattetyp (gruppera på "Händelse"-fält)
 */
function calculateTaxTypeStatistics(transactions: any[]): TaxTypeStatistics[] {
  // Gruppera per skattetyp (normalisera händelse-namn)
  const grouped = new Map<string, any[]>();
  
  for (const txn of transactions) {
    const event = String(txn.event || '').trim();
    if (!event) continue;
    
    // Normalisera skattetyp (ex: "Arbetsgivaravgifter" → "Arbetsgivaravgifter")
    const taxType = normalizeTaxType(event);
    
    if (!grouped.has(taxType)) {
      grouped.set(taxType, []);
    }
    grouped.get(taxType)!.push(txn);
  }

  const results: TaxTypeStatistics[] = [];
  
  for (const [taxType, txns] of grouped) {
    // Beräkna månadsvis breakdown
    const monthlyMap = new Map<string, number[]>();
    
    for (const txn of txns) {
      const amount = Math.abs(txn.amount || 0);
      if (amount === 0) continue;
      
      const month = txn.date?.substring(0, 7) || 'unknown'; // "2024-01"
      if (!monthlyMap.has(month)) {
        monthlyMap.set(month, []);
      }
      monthlyMap.get(month)!.push(amount);
    }
    
    // Beräkna statistik för skattetypen
    const amounts = txns.map(t => Math.abs(t.amount || 0)).filter(a => a > 0);
    
    if (amounts.length === 0) continue;
    
    const sum = amounts.reduce((a, b) => a + b, 0);
    const mean = sum / amounts.length;
    const sortedAmounts = [...amounts].sort((a, b) => a - b);
    const median = sortedAmounts[Math.floor(sortedAmounts.length / 2)] || 0;
    
    // Standard deviation
    const variance = amounts.reduce((acc, val) => acc + Math.pow(val - mean, 2), 0) / amounts.length;
    const stdDev = Math.sqrt(variance);
    
    // Coefficient of variation (skydda mot division med 0)
    const cv = mean > 0 ? stdDev / mean : 0;
    
    // Monthly breakdown
    const monthlyBreakdown: Record<string, any> = {};
    for (const [month, vals] of monthlyMap) {
      const total = vals.reduce((a, b) => a + b, 0);
      monthlyBreakdown[month] = {
        count: vals.length,
        total,
        average: vals.length > 0 ? total / vals.length : 0
      };
    }
    
    results.push({
      tax_type: taxType,
      count: amounts.length,
      monthly_median: Math.round(median),
      monthly_average: Math.round(mean),
      std_deviation: Math.round(stdDev),
      min: Math.round(Math.min(...amounts)),
      max: Math.round(Math.max(...amounts)),
      coefficient_of_variation: parseFloat(cv.toFixed(3)),
      monthly_breakdown: monthlyBreakdown
    });
  }
  
  return results.sort((a, b) => b.count - a.count);
}

/**
 * Normalisera skattetyp-namn för konsekvent gruppering
 */
function normalizeTaxType(event: string): string {
  const lower = event.toLowerCase();
  
  // Gruppera liknande händelser
  if (lower.includes('arbetsgivar') || lower.includes('ag-avgift')) {
    return 'Arbetsgivaravgifter';
  }
  if (lower.includes('preliminär') || lower.includes('prelim')) {
    return 'Preliminärskatt';
  }
  if (lower.includes('moms') || lower.includes('vat')) {
    return 'Moms';
  }
  if (lower.includes('ränta')) {
    return 'Räntor';
  }
  if (lower.includes('avgift') && !lower.includes('arbetsgivar')) {
    return 'Avgifter';
  }
  
  // Default: behåll original (kapitalisera första bokstaven)
  return event.charAt(0).toUpperCase() + event.slice(1);
}

/**
 * Beräkna betalningsbeteende (sena betalningar, avgifter, etc)
 */
function calculatePaymentBehaviorStats(transactions: any[]): PaymentBehaviorStats {
  let latePayments = 0;
  let onTimePayments = 0;
  let totalDaysLate = 0;
  let maxDaysLate = 0;
  let interestCharges = 0;
  let feeCharges = 0;
  
  for (const txn of transactions) {
    const event = String(txn.event || '').toLowerCase();
    
    // Identifiera räntor och avgifter
    if (event.includes('ränta') || event.includes('ranta')) {
      interestCharges++;
    }
    if (event.includes('avgift')) {
      feeCharges++;
    }
    
    // Identifiera sena betalningar (om due_date finns)
    if (txn.due_date && txn.date) {
      try {
        const dueDate = new Date(txn.due_date);
        const paymentDate = new Date(txn.date);
        
        if (!isNaN(dueDate.getTime()) && !isNaN(paymentDate.getTime())) {
          const daysLate = Math.floor((paymentDate.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24));
          
          if (daysLate > 0) {
            latePayments++;
            totalDaysLate += daysLate;
            maxDaysLate = Math.max(maxDaysLate, daysLate);
          } else {
            onTimePayments++;
          }
        }
      } catch (e) {
        // Ignorera ogiltiga datum
      }
    }
  }
  
  const totalPayments = latePayments + onTimePayments;
  const avgDaysLate = latePayments > 0 ? totalDaysLate / latePayments : 0;
  
  return {
    total_payments: totalPayments,
    late_payments: latePayments,
    on_time_payments: onTimePayments,
    average_days_late: parseFloat(avgDaysLate.toFixed(1)),
    max_days_late: maxDaysLate,
    interest_charges_count: interestCharges,
    fee_charges_count: feeCharges
  };
}

function getEmptyStatistics(): StatisticalSummary {
  return {
    tax_types: [],
    payment_behavior: {
      total_payments: 0,
      late_payments: 0,
      on_time_payments: 0,
      average_days_late: 0,
      max_days_late: 0,
      interest_charges_count: 0,
      fee_charges_count: 0
    },
    analysis_period: {
      start_date: 'unknown',
      end_date: 'unknown',
      months_covered: 0
    }
  };
}
